// The browser's current position, as a promise.
//
// Several INC actions are refused by the API without a location — checking in,
// and every upload of check-in media (the visit verification photo and the
// safety verification photos / videos). This is the one place that asks the
// browser for it and turns the ways that can fail into a message a person can
// act on; the caller just awaits it and shows `error.message`.

export interface BrowserLocation {
  latitude: number;
  longitude: number;
  // Radius of the fix in metres, as the device reports it.
  accuracy?: number;
}

// `action` finishes the sentence "…cannot be <action> without it", e.g.
// "uploaded" or "started".
export const getBrowserLocation = (action = "recorded"): Promise<BrowserLocation> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This browser cannot report a location. Use the mobile app instead."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? `Location permission was denied. Allow location access for this site and try again — this cannot be ${action} without it.`
              : "Could not get a location fix. Move somewhere with a clearer signal and try again.",
          ),
        ),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });
